/**
 * Throttles a continuous control (a slider drag, a colour picker): sends at
 * most every `every` ms, leading and trailing, so the latest value is always
 * sent. The appliance coalesces anything still queued (ADR-0009).
 */
export class Throttle<T> {
  private last = Number.NEGATIVE_INFINITY;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private latest: { value: T } | undefined;

  constructor(
    private readonly every: number,
    private send: (value: T) => void,
    private readonly now: () => number = Date.now,
  ) {}

  /** Replaces the send function (e.g. a re-rendered callback). */
  setSend(send: (value: T) => void) {
    this.send = send;
  }

  /** Queues value; immediate sends it now (e.g. when a drag ends). */
  push(value: T, immediate = false) {
    this.latest = { value };
    const wait = this.every - (this.now() - this.last);
    if (immediate || wait <= 0) {
      this.flush();
    } else {
      this.timer ??= setTimeout(() => {
        this.flush();
      }, wait);
    }
  }

  private flush() {
    clearTimeout(this.timer);
    this.timer = undefined;
    const pending = this.latest;
    this.latest = undefined;
    if (!pending) return;
    this.last = this.now();
    this.send(pending.value);
  }

  /** Drops anything pending (unmount). */
  cancel() {
    clearTimeout(this.timer);
    this.timer = undefined;
    this.latest = undefined;
  }
}
